import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { FileText, Loader2, MessageSquare, Pencil, Plus, RefreshCw, Search, Trash2, Upload } from 'lucide-react';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useReadOnly } from '../../contexts/ReadOnlyContext';
import { ConfirmationModal, PageLoader, Pagination } from '../../components/common';
import TrainingUploadModal from '../../components/expert/TrainingUploadModal';
import SavedAnswerModal from '../../components/expert/SavedAnswerModal';
import {
  EmptyState,
  ErrorBanner,
  PAGE_SIZE,
  PageHeader,
  Tabs,
  btnPrimary,
  btnSecondary,
  inputClass,
  useDebouncedValue,
} from '../../components/expert/ui';
import {
  SavedAnswer,
  TrainingDoc,
  expertAgentApi,
  expertKeys,
  formatDate,
  getExpertErrorMessage,
  isRequestCancelled,
} from '../../lib/domain-expert-api';

type TrainingTab = 'documents' | 'answers';

const onMutationError = (err: unknown) => {
  if (isRequestCancelled(err)) return;
  toast.error(getExpertErrorMessage(err));
};

function PagedFooter({ page, total, onPageChange, label }: { page: number; total: number; onPageChange: (p: number) => void; label: string }) {
  if (total <= PAGE_SIZE) return null;
  return (
    <Pagination
      className="mt-4"
      page={page}
      totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))}
      onPageChange={onPageChange}
      rangeStart={(page - 1) * PAGE_SIZE + 1}
      rangeEnd={Math.min(page * PAGE_SIZE, total)}
      totalItems={total}
      itemLabel={label}
    />
  );
}

function DocumentStatus({ doc }: { doc: TrainingDoc }) {
  if (doc.status === 'processing') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">
        <Loader2 className="h-3 w-3 animate-spin" />
        Processing
      </span>
    );
  }
  if (doc.status === 'ready') {
    return <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">Ready</span>;
  }
  return (
    <div>
      <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">Couldn't process</span>
      {doc.error && <p className="mt-1 max-w-xs whitespace-normal text-xs text-gray-500">{doc.error}</p>}
    </div>
  );
}

function DocumentsTab({ enabled, readOnly }: { enabled: boolean; readOnly: boolean }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim());
  const [uploadOpen, setUploadOpen] = useState(false);
  const [toRemove, setToRemove] = useState<TrainingDoc | null>(null);

  const params = { search: search || undefined, skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE };
  const { data, isInitialLoading, isError, error } = useQuery({
    queryKey: expertKeys.documents(params),
    queryFn: () => expertAgentApi.listDocuments(params),
    enabled,
    keepPreviousData: true,
    refetchInterval: (current) =>
      current && ((current.counts?.processing ?? 0) > 0 || current.items.some((d) => d.status === 'processing')) ? 5000 : false,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['expert', 'documents'] });

  const retryMutation = useMutation({
    mutationFn: (id: string) => expertAgentApi.retryDocument(id),
    onSuccess: () => {
      invalidate();
      toast.success('Trying again');
    },
    onError: onMutationError,
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => expertAgentApi.deleteDocument(id),
    onSuccess: () => {
      invalidate();
      toast.success('Document removed');
      setToRemove(null);
    },
    onError: (err) => {
      onMutationError(err);
      setToRemove(null);
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  if (isInitialLoading) return <PageLoader fullScreen={false} />;

  return (
    <div>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-600">Add PDFs or text files your agent should learn from.</p>
        <button type="button" onClick={() => setUploadOpen(true)} disabled={readOnly} className={btnPrimary}>
          <Upload className="mr-2 h-4 w-4" />
          Upload documents
        </button>
      </div>

      {(total > 0 || search) && (
        <div className="relative mt-4 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            placeholder="Search documents"
            aria-label="Search documents"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            className={`${inputClass} pl-9`}
          />
        </div>
      )}

      {isError && <ErrorBanner message={getExpertErrorMessage(error, "We couldn't load your documents.")} />}

      <div className="mt-4 overflow-x-auto bg-white shadow ring-1 ring-black ring-opacity-5">
        {items.length === 0 ? (
          search ? (
            <EmptyState title="No documents match your search." />
          ) : (
            <EmptyState icon={<FileText className="h-12 w-12" />} title="No documents yet.">
              Add PDFs or text files your agent should learn from.
            </EmptyState>
          )
        ) : (
          <table className="w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                {['Name', 'Status', 'Added'].map((heading) => (
                  <th key={heading} scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {heading}
                  </th>
                ))}
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {items.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 flex-shrink-0 text-gray-400" />
                      <span className="font-medium text-gray-900">{doc.title || doc.file_name}</span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm">
                    <DocumentStatus doc={doc} />
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{formatDate(doc.created_at)}</td>
                  <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                    {!readOnly && (
                      <div className="flex items-center justify-end gap-3">
                        {doc.status === 'failed' && (
                          <button
                            type="button"
                            onClick={() => retryMutation.mutate(doc.id)}
                            disabled={retryMutation.isLoading}
                            className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                          >
                            Try again
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setToRemove(doc)}
                          className="text-gray-500 hover:text-red-600"
                          aria-label={`Remove ${doc.title || doc.file_name}`}
                          title="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <PagedFooter page={page} total={total} onPageChange={setPage} label="documents" />

      <TrainingUploadModal isOpen={uploadOpen} onClose={() => setUploadOpen(false)} readOnly={readOnly} />

      <ConfirmationModal
        isOpen={!!toRemove}
        onClose={() => !removeMutation.isLoading && setToRemove(null)}
        onConfirm={() => toRemove && removeMutation.mutate(toRemove.id)}
        title="Remove document"
        message={`Your agent will stop using "${toRemove?.title || toRemove?.file_name || ''}". This can't be undone.`}
        confirmText="Remove"
        isLoading={removeMutation.isLoading}
      />
    </div>
  );
}

const answerPreview = (text: string) => {
  const plain = text.replace(/[#*_`>[\]]/g, '').replace(/\s+/g, ' ').trim();
  return plain.length > 180 ? `${plain.slice(0, 180)}…` : plain;
};

function SavedAnswersTab({ enabled, readOnly }: { enabled: boolean; readOnly: boolean }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim());
  const [editing, setEditing] = useState<SavedAnswer | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [toRemove, setToRemove] = useState<SavedAnswer | null>(null);

  const params = { search: search || undefined, skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE };
  const { data, isInitialLoading, isError, error } = useQuery({
    queryKey: expertKeys.answers(params),
    queryFn: () => expertAgentApi.listAnswers(params),
    enabled,
    keepPreviousData: true,
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => expertAgentApi.deleteAnswer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expert', 'answers'] });
      toast.success('Answer removed');
      setToRemove(null);
    },
    onError: (err) => {
      onMutationError(err);
      setToRemove(null);
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };

  if (isInitialLoading) return <PageLoader fullScreen={false} />;

  return (
    <div>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-600">When a user asks a question like one of these, your agent gives this exact answer.</p>
        <button type="button" onClick={openAdd} disabled={readOnly} className={`${btnPrimary} flex-shrink-0`}>
          <Plus className="mr-2 h-4 w-4" />
          Add answer
        </button>
      </div>

      {(total > 0 || search) && (
        <div className="relative mt-4 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            placeholder="Search saved answers"
            aria-label="Search saved answers"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            className={`${inputClass} pl-9`}
          />
        </div>
      )}

      {isError && <ErrorBanner message={getExpertErrorMessage(error, "We couldn't load your saved answers.")} />}

      <div className="mt-4 bg-white shadow ring-1 ring-black ring-opacity-5">
        {items.length === 0 ? (
          search ? (
            <EmptyState title="No saved answers match your search." />
          ) : (
            <EmptyState icon={<MessageSquare className="h-12 w-12" />} title="No saved answers yet.">
              Add a question and the exact answer your agent should give.
            </EmptyState>
          )
        ) : (
          <ul className="divide-y divide-gray-200">
            {items.map((item) => (
              <li key={item.id} className="flex items-start justify-between gap-4 px-6 py-4 hover:bg-gray-50">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">{item.question}</p>
                  <p className="mt-1 text-sm text-gray-600">{answerPreview(item.answer)}</p>
                  <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    {item.source === 'flag_correction' && (
                      <span className="inline-flex rounded-full bg-indigo-50 px-2 py-0.5 font-medium text-indigo-700">From a flagged answer</span>
                    )}
                    <span>Updated {formatDate(item.updated_at || item.created_at)}</span>
                  </p>
                </div>
                {!readOnly && (
                  <div className="flex flex-shrink-0 items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(item);
                        setModalOpen(true);
                      }}
                      className="text-gray-500 hover:text-indigo-700"
                      aria-label="Edit answer"
                      title="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setToRemove(item)}
                      className="text-gray-500 hover:text-red-600"
                      aria-label="Remove answer"
                      title="Remove"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <PagedFooter page={page} total={total} onPageChange={setPage} label="saved answers" />

      <SavedAnswerModal isOpen={modalOpen} onClose={() => setModalOpen(false)} answer={editing} readOnly={readOnly} />

      <ConfirmationModal
        isOpen={!!toRemove}
        onClose={() => !removeMutation.isLoading && setToRemove(null)}
        onConfirm={() => toRemove && removeMutation.mutate(toRemove.id)}
        title="Remove saved answer"
        message="Your agent will stop giving this answer. This can't be undone."
        confirmText="Remove"
        isLoading={removeMutation.isLoading}
      />
    </div>
  );
}

export default function TrainingPage() {
  const queryClient = useQueryClient();
  const { hasExpert } = useWorkspace();
  const { isReadOnly } = useReadOnly();
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshOpen, setRefreshOpen] = useState(false);
  const tab: TrainingTab = searchParams.get('tab') === 'answers' ? 'answers' : 'documents';

  const changeTab = (next: TrainingTab) => {
    setSearchParams(next === 'answers' ? { tab: 'answers' } : {}, { replace: true });
  };

  const refreshMutation = useMutation({
    mutationFn: () => expertAgentApi.refreshTraining(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expert', 'documents'] });
      queryClient.invalidateQueries({ queryKey: ['expert', 'answers'] });
      toast.success('Refreshing training');
      setRefreshOpen(false);
    },
    onError: (err) => {
      onMutationError(err);
      setRefreshOpen(false);
    },
  });

  return (
    <div>
      <PageHeader
        title="Training"
        description="What your agent knows and the answers it should give."
        actions={
          <button
            type="button"
            onClick={() => setRefreshOpen(true)}
            disabled={isReadOnly || !hasExpert || refreshMutation.isLoading}
            className={btnSecondary}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshMutation.isLoading ? 'animate-spin' : ''}`} />
            Refresh training
          </button>
        }
      />
      <div className="mt-6">
        <Tabs
          value={tab}
          onChange={changeTab}
          tabs={[
            { value: 'documents', label: 'Documents' },
            { value: 'answers', label: 'Saved answers' },
          ]}
        />
      </div>
      {tab === 'documents' ? (
        <DocumentsTab enabled={hasExpert} readOnly={isReadOnly} />
      ) : (
        <SavedAnswersTab enabled={hasExpert} readOnly={isReadOnly} />
      )}

      <ConfirmationModal
        isOpen={refreshOpen}
        onClose={() => !refreshMutation.isLoading && setRefreshOpen(false)}
        onConfirm={() => refreshMutation.mutate()}
        title="Refresh training"
        message="Your agent re-learns all documents and saved answers. Documents show Processing until they're done. Your agent keeps answering in the meantime."
        confirmText="Refresh"
        type="info"
        isLoading={refreshMutation.isLoading}
      />
    </div>
  );
}
